import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import type { AgGridReact } from 'ag-grid-react'
import type {
  DagRowRead,
  DagRowImages,
  ParentLineItemInfo,
  RowPatchResult,
} from '@/types/dag'
import type { PlanDetailItem } from '@/types'

export interface RowData {
  id: string
  /** Raw cells map from DagRowRead */
  cells?: Record<string, unknown>
  images?: DagRowImages | null
  row_version?: number
  row_index?: number
  line_ref_id?: string
  comment_count?: number
  parent_line_item?: ParentLineItemInfo | null
  isOptionRow?: boolean
  option?: {
    id: string
    name: string
    imageUrl?: string
    colorName?: string
  }
  parentId?: string
}

export interface EditingCell {
  itemId: string
  field: string
}

export interface DialogState {
  copyOpen: boolean
  deleteOpen: boolean
  selectedItemId: string | null
}

/**
 * Converts a DagRowRead into a RowData for the grid.
 */
export function dagRowToRowData(row: DagRowRead): RowData {
  return {
    id: row.id,
    cells: row.cells,
    images: row.images ?? null,
    row_version: row.row_version,
    row_index: row.row_index,
    line_ref_id: row.line_ref_id,
    comment_count: row.comment_count,
    parent_line_item: row.parent_line_item ?? null,
  }
}

/**
 * Adapter: extracts a PlanDetailItem-like shape from RowData for drawers/dialogs.
 */
export function rowDataToPlanDetailItem(row: RowData): PlanDetailItem {
  const c = row.cells ?? {}
  const hierarchyParts = [
    c['division'],
    c['department'],
    c['class'],
    c['subclass'],
  ]
    .filter(Boolean)
    .map(String)
  return {
    id: row.id,
    skuIdentifier: hierarchyParts.join('-') || '',
    skuNumber: String(c['plan_sku_num'] ?? ''),
    description: String(c['description'] ?? ''),
    status: (c['workflow_status'] as PlanDetailItem['status']) ?? 'IN_PROGRESS',
    division: (c['division'] as string) ?? null,
    department: (c['department'] as string) ?? null,
    class_: (c['class'] as string) ?? null,
    subclass: (c['subclass'] as string) ?? null,
    vendorName: (c['vendor_name'] as string) ?? null,
    targetRetail: (c['target_retail'] as string) ?? null,
    targetCost: (c['target_cost'] as string) ?? null,
    targetMargin: (c['target_margin'] as string) ?? null,
    landedCost: (c['dc_landed_cost'] as number) ?? null,
    tyPlanUnits: (c['ty_plan_units'] as number) ?? null,
    lySalesUnits: (c['ly_sales_units'] as number) ?? null,
    lySellThrough: (c['ly_sell_through'] as string) ?? null,
    storeModelId: (c['store_model_id'] as string) ?? null,
    activeStoreGroupIds: (c['active_store_group_ids'] as string[]) ?? [],
    variantCount: 1,
  }
}

export function useProductsTableState(rows: DagRowRead[]) {
  // Store IDs for selection tracking
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set())
  // Cache of selected items' data (survives page changes, stays fresh with edits)
  const [selectedItemsCache, setSelectedItemsCache] = useState<
    Map<string, DagRowRead>
  >(new Map())
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null)

  // Dialog states
  const [dialogState, setDialogState] = useState<DialogState>({
    copyOpen: false,
    deleteOpen: false,
    selectedItemId: null,
  })

  // Refs for performance optimization (avoid re-creating columnDefs)
  const selectedItemIdsRef = useRef<Set<string>>(selectedItemIds)
  const selectedItemsCacheRef =
    useRef<Map<string, DagRowRead>>(selectedItemsCache)
  const allSelectedRef = useRef(false)
  const someSelectedRef = useRef(false)
  const gridRef = useRef<AgGridReact<RowData>>(null)

  // Update cache when current page rows change
  useEffect(() => {
    setSelectedItemsCache(prev => {
      const newCache = new Map(prev)
      for (const row of rows) {
        if (selectedItemIds.has(row.id)) {
          newCache.set(row.id, row)
        }
      }
      for (const id of newCache.keys()) {
        if (!selectedItemIds.has(id)) {
          newCache.delete(id)
        }
      }
      return newCache
    })
  }, [rows, selectedItemIds])

  // Derive PlanDetailItem[] from cache for bulk actions bar
  const selectedItemsData = useMemo(
    () =>
      Array.from(selectedItemsCache.values()).map(r =>
        rowDataToPlanDetailItem(dagRowToRowData(r))
      ),
    [selectedItemsCache]
  )

  // Update refs and refresh grid UI when selection changes
  useEffect(() => {
    selectedItemIdsRef.current = selectedItemIds
    selectedItemsCacheRef.current = selectedItemsCache
  }, [selectedItemIds, selectedItemsCache])

  useEffect(() => {
    const currentPageSelectedCount = rows.filter(row =>
      selectedItemIds.has(row.id)
    ).length
    allSelectedRef.current =
      rows.length > 0 && currentPageSelectedCount === rows.length
    someSelectedRef.current =
      currentPageSelectedCount > 0 && currentPageSelectedCount < rows.length

    if (gridRef.current?.api) {
      gridRef.current.api.refreshHeader()
      gridRef.current.api.refreshCells({ columns: ['name'], force: true })
    }
  }, [selectedItemIds, rows])

  // Selection handlers
  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedItemIds(prev => {
          const newSelected = new Set(prev)
          rows.forEach(row => newSelected.add(row.id))
          return newSelected
        })
        setSelectedItemsCache(prev => {
          const newCache = new Map(prev)
          rows.forEach(row => newCache.set(row.id, row))
          return newCache
        })
      } else {
        setSelectedItemIds(prev => {
          const newSelected = new Set(prev)
          rows.forEach(row => newSelected.delete(row.id))
          return newSelected
        })
      }
    },
    [rows]
  )

  const handleSelectItem = useCallback(
    (itemId: string, checked: boolean) => {
      const row = rows.find(r => r.id === itemId)
      setSelectedItemIds(prev => {
        const newSelected = new Set(prev)
        if (checked) {
          newSelected.add(itemId)
        } else {
          newSelected.delete(itemId)
        }
        return newSelected
      })
      if (checked && row) {
        setSelectedItemsCache(prev => new Map(prev).set(itemId, row))
      }
    },
    [rows]
  )

  const handleClearSelection = useCallback(() => {
    setSelectedItemIds(new Set())
    setSelectedItemsCache(new Map())
  }, [])

  const applyPatchedRowsToSelectionCache = useCallback(
    (patches: RowPatchResult[]) => {
      setSelectedItemsCache(prev => {
        let changed = false
        const next = new Map(prev)

        for (const patch of patches) {
          const existing = next.get(patch.row_id)
          if (!existing) continue

          const cellUpdates = Object.fromEntries(
            patch.cells.map(cell => [cell.column_id, cell.value])
          )

          next.set(patch.row_id, {
            ...existing,
            row_version: patch.new_version,
            cells: {
              ...existing.cells,
              ...cellUpdates,
            },
          })
          changed = true
        }

        return changed ? next : prev
      })
    },
    []
  )

  // Expand/collapse handlers
  const handleToggleExpand = useCallback((itemId: string) => {
    setExpandedItems(prev => {
      const newExpanded = new Set(prev)
      if (newExpanded.has(itemId)) {
        newExpanded.delete(itemId)
      } else {
        newExpanded.add(itemId)
      }
      return newExpanded
    })
  }, [])

  // Edit handlers
  const handleStartEdit = useCallback((itemId: string, field: string) => {
    setEditingCell({ itemId, field })
  }, [])

  const clearEditingCell = useCallback(() => {
    setEditingCell(null)
  }, [])

  const handleCancelEdit = clearEditingCell

  // Dialog handlers
  const openCopyDialog = useCallback((itemId: string) => {
    setDialogState(prev => ({
      ...prev,
      copyOpen: true,
      selectedItemId: itemId,
    }))
  }, [])

  const closeCopyDialog = useCallback(() => {
    setDialogState(prev => ({
      ...prev,
      copyOpen: false,
      selectedItemId: null,
    }))
  }, [])

  const openDeleteDialog = useCallback((itemId: string) => {
    setDialogState(prev => ({
      ...prev,
      deleteOpen: true,
      selectedItemId: itemId,
    }))
  }, [])

  const closeDeleteDialog = useCallback(() => {
    setDialogState(prev => ({
      ...prev,
      deleteOpen: false,
      selectedItemId: null,
    }))
  }, [])

  const handleAutoSaveBeforeMenu = useCallback(() => {
    if (editingCell) {
      const activeElement = document.activeElement as HTMLElement
      activeElement?.blur()
    }
  }, [editingCell])

  return {
    // State
    selectedItemIds,
    selectedItemsData,
    expandedItems,
    editingCell,
    dialogState,

    // Refs
    selectedItemIdsRef,
    selectedItemsCacheRef,
    allSelectedRef,
    someSelectedRef,
    gridRef,

    // Selection handlers
    handleSelectAll,
    handleSelectItem,
    handleClearSelection,
    applyPatchedRowsToSelectionCache,

    // Expand handlers
    handleToggleExpand,

    // Edit handlers
    handleStartEdit,
    handleCancelEdit,
    clearEditingCell,

    // Dialog handlers
    openCopyDialog,
    closeCopyDialog,
    openDeleteDialog,
    closeDeleteDialog,

    // Utility
    handleAutoSaveBeforeMenu,
  }
}
